'use client';

import { type CSSProperties, type ReactNode, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMounted } from '../hooks/use-mounted';
import { takeBodyStyles } from '../lib/body-styles';
import { subscribeToModals, topModal } from '../lib/modal-stack';

/**
 * Keeps its children usable while a modal dialog is open, for what has to
 * answer the reader over one: toasts. A modal makes everything outside it
 * inert, so the children render inside the newest dialog, and in a popover
 * of their own, which the top layer paints above the dialog whatever its
 * transform or overflow. With no dialog open they sit at the end of <body>,
 * still in the top layer.
 *
 * Anything here covers every dialog and every z-index. The consent banner
 * stays out: on a phone it would cover a bottom drawer's buttons.
 *
 * Moving between dialogs keeps the children's state. Where the browser can't
 * move an element in place (Element.moveBefore), their running animations
 * start over.
 */
export function AboveDialogs({ children }: { children?: ReactNode }) {
   const [host] = useState(createHost);
   const isMounted = useMounted();

   useLayoutEffect(() => {
      if (host) return followTopModal(host);
   }, [host]);

   // Not on the server, nor in the render that hydrates it.
   return host && isMounted ? createPortal(children, host) : null;
}

function createHost() {
   if (typeof document === 'undefined') return null;
   const host = document.createElement('div');
   host.dataset.aboveDialogs = '';
   host.popover = 'manual';
   Object.assign(host.style, HOST_STYLE);
   return host;
}

function followTopModal(host: HTMLElement) {
   let hasBodyStyles = false;
   const place = () => {
      const modal = topModal();
      moveTo(modal ?? document.body, host);
      // Inside a dialog it would inherit the dialog's text styles. Copied on
      // the first move into one, not on every page load.
      if (modal && !hasBodyStyles) {
         takeBodyStyles(host);
         host.style.color = getComputedStyle(document.body).color;
         hasBodyStyles = true;
      }
      raise(host);
   };
   place();
   const unsubscribe = subscribeToModals(place);
   return () => {
      unsubscribe();
      host.remove();
   };
}

// The browser draws a popover as a bordered, padded box in the middle of the
// screen; this one is an invisible full-screen layer that lets presses
// through to whatever is under it, except on its children.
const HOST_STYLE: CSSProperties = {
   position: 'fixed',
   inset: '0',
   width: 'auto',
   height: 'auto',
   margin: '0',
   padding: '0',
   border: '0',
   background: 'transparent',
   overflow: 'visible',
   pointerEvents: 'none',
};

// The top layer stacks in the order things were shown, so the layer is shown
// again after every dialog that opens.
function raise(element: HTMLElement) {
   // jsdom has no Popover API.
   if (typeof element.showPopover !== 'function') return;
   if (element.matches(':popover-open')) element.hidePopover();
   element.showPopover();
}

function moveTo(parent: Element, element: HTMLElement) {
   if (element.parentElement === parent) return;
   const movable = parent as Element & { moveBefore?: (node: Node, child: Node | null) => void };
   if (movable.moveBefore && element.isConnected) {
      try {
         movable.moveBefore(element, null);
         return;
      } catch {
         // Moves across documents or shadow roots are refused; append instead.
      }
   }
   parent.append(element);
}
